/**
 * Store QR Code Generator Component
 * Generates and displays QR codes for seller store links
 */
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { QrCode, Download, Copy, Loader2, Share2, Palette } from 'lucide-react';

// QR Code generation using qrcode library
const generateQRCodeDataURL = async (text, options = {}) => {
  const { size = 256, color = '#000000', backgroundColor = '#ffffff' } = options;
  
  // Use canvas-based QR code generation
  const QRCode = (await import('qrcode')).default;
  
  const dataUrl = await QRCode.toDataURL(text, {
    width: size,
    margin: 2,
    color: {
      dark: color,
      light: backgroundColor
    },
    errorCorrectionLevel: 'H'
  });
  
  return dataUrl;
};

const StoreQRCode = ({ 
  storeId, 
  storeName, 
  storeSlug,
  className = '' 
}) => {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [qrSize, setQrSize] = useState(256);
  const [qrColor, setQrColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const canvasRef = useRef(null);

  // Generate the store URL
  const storeUrl = storeSlug 
    ? `${window.location.origin}/store/${storeSlug}`
    : `${window.location.origin}/store/${storeId}`;

  // Generate QR code when component mounts or settings change
  useEffect(() => {
    generateQR();
  }, [storeUrl, qrSize, qrColor, bgColor]);

  const generateQR = async () => {
    setLoading(true);
    setError(null);
    try {
      const dataUrl = await generateQRCodeDataURL(storeUrl, {
        size: qrSize,
        color: qrColor,
        backgroundColor: bgColor
      });
      setQrDataUrl(dataUrl);
    } catch (err) {
      console.error('QR generation error:', err);
      setError('Failed to generate QR code');
      toast.error('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;
    
    const link = document.createElement('a');
    link.download = `${storeName || 'store'}-qr-code.png`;
    link.href = qrDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded!');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(storeUrl);
      toast.success('Store link copied!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${storeName} on Liantiao`,
          text: `Check out ${storeName} on Liantiao - Jamaica's Marketplace!`,
          url: storeUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const colorPresets = [
    { name: 'Classic Black', color: '#000000', bg: '#ffffff' },
    { name: 'Jamaica Green', color: '#009B3A', bg: '#ffffff' },
    { name: 'Jamaica Gold', color: '#FED100', bg: '#000000' },
    { name: 'Liantiao Primary', color: '#22c55e', bg: '#ffffff' },
    { name: 'Dark Mode', color: '#ffffff', bg: '#1e293b' },
  ];

  return (
    <Card className={`${className}`} data-testid="store-qr-code-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="w-5 h-5" />
          Store QR Code
        </CardTitle>
        <CardDescription>
          Generate a QR code for your store link. Customers can scan it to visit your store.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* QR Code Display */}
        <div className="flex justify-center">
          <div 
            className="relative p-4 bg-white rounded-xl shadow-md border"
            style={{ backgroundColor: bgColor }}
          >
            {loading ? (
              <div className="w-64 h-64 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="w-64 h-64 flex items-center justify-center text-destructive">
                {error}
              </div>
            ) : qrDataUrl ? (
              <img 
                src={qrDataUrl} 
                alt={`QR Code for ${storeName}`}
                className="w-64 h-64"
                data-testid="qr-code-image"
              />
            ) : null}
          </div>
        </div>

        {/* Store URL Display */}
        <div className="space-y-2">
          <Label>Store URL</Label>
          <div className="flex gap-2">
            <Input 
              value={storeUrl} 
              readOnly 
              className="font-mono text-sm"
              data-testid="store-url-input"
            />
            <Button variant="outline" size="icon" onClick={handleCopyLink} data-testid="copy-link-btn">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Color Presets */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Color Presets
          </Label>
          <div className="flex flex-wrap gap-2">
            {colorPresets.map((preset, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => {
                  setQrColor(preset.color);
                  setBgColor(preset.bg);
                }}
                data-testid={`color-preset-${index}`}
              >
                <div 
                  className="w-4 h-4 rounded mr-2 border"
                  style={{ backgroundColor: preset.color }}
                />
                {preset.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Custom Colors */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="qrColor">QR Color</Label>
            <div className="flex gap-2">
              <Input
                id="qrColor"
                type="color"
                value={qrColor}
                onChange={(e) => setQrColor(e.target.value)}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={qrColor}
                onChange={(e) => setQrColor(e.target.value)}
                className="font-mono text-sm"
                placeholder="#000000"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="bgColor">Background</Label>
            <div className="flex gap-2">
              <Input
                id="bgColor"
                type="color"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                className="font-mono text-sm"
                placeholder="#ffffff"
              />
            </div>
          </div>
        </div>

        {/* Size Selector */}
        <div className="space-y-2">
          <Label>QR Code Size</Label>
          <Select value={qrSize.toString()} onValueChange={(v) => setQrSize(parseInt(v))}>
            <SelectTrigger data-testid="qr-size-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="128">Small (128px)</SelectItem>
              <SelectItem value="256">Medium (256px)</SelectItem>
              <SelectItem value="512">Large (512px)</SelectItem>
              <SelectItem value="1024">Extra Large (1024px)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={handleDownload} disabled={loading || !qrDataUrl} className="flex-1" data-testid="download-qr-btn">
            <Download className="w-4 h-4 mr-2" />
            Download QR
          </Button>
          <Button variant="outline" onClick={handleShare} className="flex-1" data-testid="share-qr-btn">
            <Share2 className="w-4 h-4 mr-2" />
            Share Link
          </Button>
        </div>

        {/* Usage Tips */}
        <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
          <p className="font-medium mb-2">💡 Tips for using your QR code:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Print it on business cards, flyers, or receipts</li>
            <li>Display it in your physical store</li>
            <li>Add it to your social media posts</li>
            <li>Use Jamaica Gold for a patriotic look!</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default StoreQRCode;
